import React, { useState, useEffect } from 'react';
import { useCart } from '../../context/CartContext';
import { useLanguage } from '../../context/LanguageContext';
import '../DetailedView.css';
import QuoteRequestForm from '../QuoteRequestForm';
import { seasonalDates } from '../../data/seasonaldates';
import { formatAandeelDisplay } from '../../utils/aandeelDisplay';

const AudioDetailView = ({ selectedRow, columns }) => {
  const [bereikValue, setBereikValue] = useState('');
  const [totalPrice, setTotalPrice] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const { addToCart } = useCart();
  const { t } = useLanguage();
  
  // Check if pricing is CPM-based or fixed
  const isCpmBased = selectedRow['Tariefbasis'] === 'CPM';
  
  // Reset bereikValue when selectedRow changes
  useEffect(() => {
    setBereikValue('');
    setTotalPrice(null);
    setAddedToCart(false);
  }, [selectedRow]);
  
  // Calculate total price when bereikValue changes
  const calculateTotalPrice = (value) => {
    // Only calculate if it's CPM-based
    if (!isCpmBased) {
      return;
    }
    
    const bereik = parseFloat(value);
    const basisprijs = parseDutchCurrency(selectedRow['Basisprijs'] || '0');
    const positionFactor = parseDutchCurrency(selectedRow['Positiefactor']) || 1;
    const formatFactor = parseDutchCurrency(selectedRow['Formaatfactor']) || 1;

 
    
    if (!isNaN(bereik) && !isNaN(basisprijs) && bereik > 0) {
      setTotalPrice(bereik * basisprijs * positionFactor * formatFactor);
    } else {
      setTotalPrice(null);
    }
  };

  // Function to properly parse Dutch and dot-decimal formatted currency
  const parseDutchCurrency = (currencyStr) => {
    if (currencyStr === null || currencyStr === undefined || currencyStr === '') return 0;
    const s = currencyStr.toString().replace(/€/g, '').trim();
    if (s.includes(',')) {
      return parseFloat(s.replace(/\./g, '').replace(/,/g, '.')) || 0;
    }
    const dotCount = (s.match(/\./g) || []).length;
    if (dotCount === 1 && /^\d+\.\d+$/.test(s)) {
      return parseFloat(s) || 0;
    }
    // Number or string "1975" that is really "19,75" with comma stripped (e.g. 1975 → 19.75)
    const asNum = parseFloat(s.replace(/\./g, '')) || 0;
    if (Number.isInteger(asNum) && asNum >= 1000 && asNum <= 9999) {
      return asNum / 100;
    }
    return asNum;
  };
  
  // Format price with comma as decimal separator and dot as thousands separator
  const formatPrice = (price) => {
    if (price === null || isNaN(price)) return '0,00';
    
    // Format with 2 decimal places and replace decimal point with comma
    let formattedPrice = price.toFixed(2).replace('.', ',');
    
    // Add thousands separator (dot)
    if (price >= 1000) {
      const parts = formattedPrice.split(',');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      formattedPrice = parts.join(',');
    }
    
    return formattedPrice;
  };
  
  // Parse a price string (supports € symbol, dot thousand sep, comma decimal) into a number
  const parsePriceString = (priceStr) => {
    if (priceStr === null || priceStr === undefined) return NaN;
    const cleaned = priceStr
      .toString()
      .replace(/€/g, '')
      .replace(/\./g, '')
      .replace(/,/g, '.')
      .trim();
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? NaN : parsed;
  };
  
  // Get clean basisprijs for display, preserving formatting
  const getCleanBasisprijs = () => {
    if (!selectedRow['Basisprijs']) return '0';
    // Just remove the € symbol but keep the formatting (dots and commas)
    return selectedRow['Basisprijs'].toString()
      .replace(/€/g, '')
      .trim();
  };
  
  // Get formatted basisprijs with two decimals (comma decimal, dot thousands)
  const getFormattedBasisprijs = () => {
    const raw = selectedRow['Basisprijs'];
    if (!raw) return null;
    const numeric = parseDutchCurrency(raw);
    return formatPrice(numeric);
  };
  
  const handleBereikChange = (e) => {
    const value = e.target.value;
    setBereikValue(value);
    calculateTotalPrice(value);
  };
  
  // Format number with dot as thousands separator, no decimals
  // Column name to translation key for additional info fields
  const additionalInfoColumnKeys = {
    Medium: 'medium',
    Plaatsingsdag: 'plaatsingsdag',
    Tariefbasis: 'tariefbasis',
    Bereik: 'bereik',
    Positie: 'filterLabelPositie',
    Positiefactor: 'positionFactor',
    Formaatfactor: 'formatFactor',
    Aandeel: 'filterLabelAandeel',
    Doelgroep: 'doelgroep',
    Luisteraars: 'luisteraars',
    Basisprijs: 'basisprijs',
  };

  const formatWholeNumber = (number) => {
    if (number === null || isNaN(number)) return '0';
    
    // Convert to integer and format as string
    const intValue = Math.round(number);
    let formattedNumber = intValue.toString();
    
    // Add thousands separator (dot)
    if (intValue >= 1000) {
      formattedNumber = formattedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
    
    return formattedNumber;
  };

  // Handle adding item to cart
  const handleAddToCart = () => {
    // For CPM-based pricing, validate bereik value
    if (isCpmBased && (!bereikValue || totalPrice === null)) {
      return;
    }
    
    // Create a unique ID for the cart item
    const itemId = `${selectedRow['Mediumtype'] || 'Audio'}-${selectedRow['Titel']}-${Date.now()}`;
    
    // Create cart item object
    const cartItem = {
      id: itemId,
      title: selectedRow['Titel'] || 'Unknown Station/Show',
      format: selectedRow['Formaat'] || 'Standard Format',
      medium: selectedRow['Mediumtype'] || 'Audio',
      position: selectedRow['Positie'] || '',
      targetingFactor: selectedRow['Tariefbasis'] || '',
      originalData: selectedRow
    };
    
    // Add price information based on pricing model
    if (isCpmBased) {
      // For CPM, use calculated price
      cartItem.price = formatPrice(totalPrice);
      cartItem.basePrice = selectedRow['Basisprijs'] || '0';
      cartItem.bereik = bereikValue;
      cartItem.impressions = parseFloat(bereikValue) * 1000;
      cartItem.positionFactor = selectedRow['Positiefactor'] || '1';
      cartItem.formatFactor = selectedRow['Formaatfactor'] || '1';
    } else {
      // For fixed price, use the base price directly
      cartItem.price = selectedRow['Basisprijs'] || '0';
      cartItem.pricingModel = 'Fixed';
    }
    
    // Add to cart
    addToCart(cartItem);
    
    // Show success message
    setAddedToCart(true);
    
    // Reset success message after 3 seconds
    setTimeout(() => {
      setAddedToCart(false);
    }, 3000);
  };
  
  // Handle adding fixed price item to cart
  const handleAddFixedPriceToCart = () => {
    handleAddToCart();
  };

  // Handle opening the quote request form
  const handleOpenQuoteForm = () => {
    setShowQuoteForm(true);
  };
  
  // Format seasonal dates for the tooltip
  const formatSeasonalDatesInfo = () => {
    return (
      <div className="seasonal-dates-tooltip">
        <h4>Seizoen Toeslagen</h4>
        <ul>
          {seasonalDates.map(period => (
            <li key={period.id}>
              <strong>{period.name}:</strong> {period.days.length} dagen
              <br/>
              <span className="date-range">{period.days[0]} t/m {period.days[period.days.length - 1]}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="detailed-view audio-view">
      <div className="basic-details-section">
        <div className="detail-section compact">
          <strong className="detailed-field-label small">{t('stationShow')}</strong>
          <div className="detailed-field-value">{selectedRow['Titel'] || '-'}</div>
        </div>
        
        <div className="detail-section compact">
          <strong className="detailed-field-label small">{t('formatAndTimeslot')}</strong>
          <div className="detailed-field-value">
            {selectedRow['Formaat'] || '-'} {selectedRow['Tijdslot'] ? `/ ${selectedRow['Tijdslot']}` : ''}
          </div>
        </div>
        
        <div className="detail-section compact">
          <strong className="detailed-field-label small">{t('totalPrice', 'Totaal prijs')}</strong>
          <div className="detailed-field-value price"> € {getFormattedBasisprijs() ?? '-'}</div>
          {selectedRow['Opmerkingen'] && (
            <div className="detailed-field-notes">{selectedRow['Opmerkingen']}</div>
          )}
          <div className="seasonal-surcharge-info">
            <span className="info-text">Let op, prijzen zijn exclusief seizoens toeslag</span>
            <div className="icon-surcharge-info" 
                 onMouseEnter={() => setShowTooltip(true)}
                 onMouseLeave={() => setShowTooltip(false)}>
              {showTooltip && (
                <div className="seasonal-tooltip">
                  {formatSeasonalDatesInfo()}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="detail-section compact">
          <div 
            className="foldable-header" 
            onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}
          >
            <strong className="detailed-field-label small">{t('additionalInformation')}</strong>
            <span className={`fold-icon ${showAdditionalInfo ? 'open' : ''}`}>
              {showAdditionalInfo ? '−' : '+'}
            </span>
          </div>
          
          {showAdditionalInfo && (
            <div className="foldable-content">
              {columns
                .filter(column =>
                  ['Medium', 'Plaatsingsdag', 'Tariefbasis', 'Bereik', 'Positie', 'Positiefactor', 'Formaatfactor', 'Aandeel', 'Doelgroep', 'Luisteraars', 'Basisprijs'].includes(column)
                )
                .map((column, index) => {
                  const labelKey = additionalInfoColumnKeys[column];
                  const rawValue = selectedRow[column];
                  const displayValue =
                    column === 'Basisprijs' && rawValue
                      ? `€ ${formatPrice(parseDutchCurrency(rawValue))}`
                      : column === 'Aandeel' && rawValue
                        ? formatAandeelDisplay(rawValue)
                        : (rawValue || '-');
                  return (
                    <div key={index} className="detail-item">
                      <span className="detail-item-label">{labelKey ? t(labelKey) : column}:</span>
                      <span className="detail-item-value">{displayValue}</span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
      
      {isCpmBased ? (
        // CPM-based pricing calculation section
        <div className="calculation-section">
          <h4 className="calculation-title">{t('calculatePrice')}</h4>
          
          <div className="calculation-description">
            {t('enterCampaignReach')}
          </div>
          
          <div className="detail-section">
            <div className="calculation-step">
              <div className="step-content">
                <strong className="detailed-field-label small">{t('campaignReach')}</strong>
                <div className="calculator-input">
                  <input 
                    id="bereik-input"
                    type="number" 
                    value={bereikValue} 
                    onChange={handleBereikChange}
                    placeholder={t('enterValue')}
                    min="0"
                    className="small-input"
                  />
                  <span className="input-suffix">CPM</span>
                </div>
                
                {bereikValue && !isNaN(parseFloat(bereikValue)) && (
                  <div className="impressions-display">
                    <span className="impressions-value">{formatWholeNumber(parseFloat(bereikValue) * 1000)}</span>
                    <span className="impressions-label">{t('totalImpressions')}</span>
                  </div>
                )}
              </div>
            </div>
            
            {bereikValue && !isNaN(parseFloat(bereikValue)) && (
              <div className="price-result">
                <div className="price-header">{t('totalCampaignCost')}</div>
                <div className="price-content">
                  <div className="price-amount">€ {formatPrice(totalPrice)}</div>
                  <div className="price-details">
                    <div className="price-detail-item">
                      <span className="detail-label">{t('impressions')}:</span>
                      <span className="detail-value">{formatWholeNumber(parseFloat(bereikValue) * 1000)}</span>
                    </div>
                    <div className="price-detail-item">
                      <span className="detail-label">{t('baseRate')}:</span>
                      <span className="detail-value">€ {getFormattedBasisprijs() ?? '0,00'}</span>
                    </div>
                    {parseFloat(selectedRow['Positiefactor'] || 1) !== 1 && (
                      <div className="price-detail-item">
                        <span className="detail-label">{t('positionFactor')}:</span>
                        <span className="detail-value">{selectedRow['Positiefactor']}</span>
                      </div>
                    )}
                    {parseFloat(selectedRow['Formaatfactor'] || 1) !== 1 && (
                      <div className="price-detail-item">
                        <span className="detail-label">{t('formatFactor')}:</span>
                        <span className="detail-value">{selectedRow['Formaatfactor']}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="cart-actions">
                  <button 
                    className="add-to-cart-btn" 
                    onClick={handleAddToCart}
                    disabled={!bereikValue || isNaN(parseFloat(bereikValue)) || parseFloat(bereikValue) <= 0}
                  >
                    {t('addToCart')}
                  </button>
                  {/* <button 
                    className="request-quote-btn" 
                    onClick={handleOpenQuoteForm}
                  >
                    {t('requestQuote')}
                  </button> */}
                  {addedToCart && (
                    <div className="added-to-cart-message">
                      {t('addedToCart')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        // Fixed price section
        <div className="fixed-price-section">
          <h4 className="fixed-price-title">{t('fixedPriceItem')}</h4>
          
          <div className="fixed-price-description">
            {t('fixedPriceDescription')}
          </div>
          
          <div className="price-display">
            <div className="price-header">{t('itemPrice')}</div>
            <div className="price-amount">€ {selectedRow['Basisprijs'] ? formatPrice(parseDutchCurrency(selectedRow['Basisprijs'])) : '-'}</div>
          </div>
          
          <div className="cart-actions">
            <button 
              className="add-to-cart-btn" 
              onClick={handleAddFixedPriceToCart}
            >
              {t('addToCart')}
            </button>
            {/* <button 
              className="request-quote-btn" 
              onClick={handleOpenQuoteForm}
            >
              {t('requestQuote')}
            </button> */}
            {addedToCart && (
              <div className="added-to-cart-message">
                {t('addedToCart')}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Quote Request Form */}
      <QuoteRequestForm 
        isOpen={showQuoteForm}
        onClose={() => setShowQuoteForm(false)}
        selectedProduct={selectedRow}
        bereikValue={bereikValue}
      />
    </div>
  );
};

export default AudioDetailView; 